/**
 * owner user account → lessor record.
 *
 * An apartment names its owner in one of TWO columns (see ApartmentEditSheet):
 *   • apartments.lessor_id      — an explicit lessor entity, or
 *   • apartments.owner_user_id  — an owner PROFILE; "the owner is the lessor
 *                                 by default", per the editor's own copy.
 *
 * Every ownership surface (Owners list, owner detail, invoices) keys off
 * lessors, so the second form has to be resolved back to a lessor or the
 * apartment silently belongs to nobody. This is the resolver, mirroring the
 * link the invoice generator uses in reverse:
 *   property_owners.lessor_id for that user, else profiles.email → lessors.email.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const OWNER_USER_LESSOR_KEY = ["v2", "owner-user-lessor-map"] as const;

/**
 * Map of owner user_id → lessor_id. Missing entries mean that owner has no
 * lessor record at all, so apartments naming them can't join a portfolio.
 */
export async function fetchLessorIdByOwnerUser(): Promise<
  Record<string, string>
> {
  // Ordered oldest-first, and first-write-wins below: a user with several
  // property_owners rows, or two lessors sharing an email, must resolve to the
  // SAME lessor every time and to the same one the DB picks — this map decides
  // which portfolio an apartment shows up in, and `lessor_for_owner_user` (see
  // 20260802100500_apartments_normalise_lessor_from_owner_account.sql) settles
  // the same tie by `ORDER BY created_at, id`. Unordered, the two could
  // disagree and the UI would attribute an apartment to a different owner than
  // the invoice generator bills.
  const [ownersRes, profilesRes, lessorsRes] = await Promise.all([
    supabase
      .from("property_owners")
      .select("user_id, lessor_id, created_at, id")
      .order("created_at", { ascending: true })
      .order("id", { ascending: true }),
    supabase.from("profiles").select("id, email"),
    supabase
      .from("lessors")
      .select("id, email, created_at")
      .order("created_at", { ascending: true })
      .order("id", { ascending: true }),
  ]);

  const byUser: Record<string, string> = {};

  // 1. The explicit link, when an owner account was wired to a lessor.
  for (const row of (ownersRes.data ?? []) as Array<{
    user_id: string | null;
    lessor_id: string | null;
  }>) {
    if (row.user_id && row.lessor_id && !byUser[row.user_id])
      byUser[row.user_id] = row.lessor_id;
  }

  // 2. Email fallback — the same match generate_management_invoices makes when
  //    no property_owners row exists.
  const lessorByEmail = new Map<string, string>();
  for (const l of (lessorsRes.data ?? []) as Array<{
    id: string;
    email: string | null;
  }>) {
    const key = l.email?.trim().toLowerCase();
    // First (oldest) wins — overwriting would hand duplicate-email lessors to
    // whichever row PostgREST returned last.
    if (key && !lessorByEmail.has(key)) lessorByEmail.set(key, l.id);
  }
  for (const p of (profilesRes.data ?? []) as Array<{
    id: string;
    email: string | null;
  }>) {
    if (byUser[p.id] || !p.email) continue;
    const hit = lessorByEmail.get(p.email.trim().toLowerCase());
    if (hit) byUser[p.id] = hit;
  }

  return byUser;
}

export function useLessorIdByOwnerUser() {
  return useQuery({
    queryKey: OWNER_USER_LESSOR_KEY,
    queryFn: fetchLessorIdByOwnerUser,
  });
}
